import { Download, Share2, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

const DISMISS_KEY = "sgprop-mobile-install-banner-dismissed";
const CONDENSE_KEY = "sgprop-mobile-install-banner-condensed";

const readBannerPreference = (key) => {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(key) === "1" || window.sessionStorage.getItem(key) === "1";
  } catch {
    return window.sessionStorage.getItem(key) === "1";
  }
};

const writeBannerPreference = (key, value = "1") => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // fall back to session storage only
  }
  window.sessionStorage.setItem(key, value);
};

const isIosSafari = () => {
  if (typeof window === "undefined") return false;
  const userAgent = window.navigator.userAgent || "";
  const isIos = /iPhone|iPad|iPod/i.test(userAgent);
  const isWebKit = /WebKit/i.test(userAgent);
  const isOtherBrowser = /CriOS|FxiOS|EdgiOS/i.test(userAgent);
  return isIos && isWebKit && !isOtherBrowser;
};

const isStandaloneMode = () => {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
};

export const MobileInstallBanner = () => {
  const location = useLocation();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [isCondensed, setIsCondensed] = useState(false);
  const [showIosOverlay, setShowIosOverlay] = useState(false);
  const iosSafari = useMemo(() => isIosSafari(), []);
  const engagementProfile = useMemo(() => {
    const pathname = location.pathname || "/";
    if (pathname === "/advisor" || pathname.endsWith("/advisor") || pathname.includes("/listings") || pathname.includes("/financials") || pathname.includes("/calculators")) {
      return { scrollThreshold: 56 };
    }
    if (pathname.startsWith("/agent/")) {
      return { scrollThreshold: 72 };
    }
    if (pathname === "/" || pathname.startsWith("/for-agents")) {
      return { scrollThreshold: 132 };
    }
    return { scrollThreshold: 96 };
  }, [location.pathname]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    setIsDismissed(readBannerPreference(DISMISS_KEY));
    setIsCondensed(readBannerPreference(CONDENSE_KEY));

    const handleViewport = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsStandalone(isStandaloneMode());
    };

    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setDeferredPrompt(event);
    };

    const handleAppInstalled = () => {
      setDeferredPrompt(null);
      setIsStandalone(true);
      setIsDismissed(true);
    };

    handleViewport();
    window.addEventListener("resize", handleViewport);
    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);

    return () => {
      window.removeEventListener("resize", handleViewport);
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || isDismissed || isStandalone) return undefined;

    const collapseBanner = () => {
      setIsCondensed(true);
      writeBannerPreference(CONDENSE_KEY);
    };

    const handleScroll = () => {
      if (window.scrollY > engagementProfile.scrollThreshold) {
        collapseBanner();
      }
    };

    const handleFocusIn = (event) => {
      if (event.target?.closest?.('[data-testid="mobile-install-banner"]')) return;
      if (["INPUT", "SELECT", "TEXTAREA"].includes(event.target?.tagName)) {
        collapseBanner();
      }
    };

    const handlePointerEngagement = (event) => {
      if (event.target?.closest?.('[data-testid="mobile-install-banner"]')) return;
      if (event.target?.closest?.('button, a, [role="button"]')) {
        collapseBanner();
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("focusin", handleFocusIn);
    window.addEventListener("click", handlePointerEngagement, true);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("focusin", handleFocusIn);
      window.removeEventListener("click", handlePointerEngagement, true);
    };
  }, [engagementProfile.scrollThreshold, isDismissed, isStandalone]);

  const dismissBanner = () => {
    setIsDismissed(true);
    writeBannerPreference(DISMISS_KEY);
  };

  const handleInstall = async () => {
    if (iosSafari) {
      setShowIosOverlay(true);
      return;
    }

    if (!deferredPrompt) {
      return;
    }

    deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    if (choice?.outcome === "accepted") {
      dismissBanner();
    }
    setDeferredPrompt(null);
  };

  if (!isMobile || isStandalone || isDismissed) return null;

  return (
    <>
      {isCondensed ? (
        <div className="mobile-install-banner mobile-install-banner-condensed mt-2 rounded-[22px] border border-[#D4AF37]/28 bg-[#FDF5E6] px-3 py-3 shadow-[0_12px_28px_rgba(90,75,53,0.08)] md:hidden" data-testid="mobile-install-banner">
          <div className="flex items-center gap-3">
            <div className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#D4AF37]/18 text-[#8b6d16]" data-testid="mobile-install-banner-icon">
              <Download className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#8b6d16]" data-testid="mobile-install-banner-kicker">Install SG Prop Portal</p>
              <p className="text-sm leading-5 text-[#3f3a32]" data-testid="mobile-install-banner-copy">Keep this saved for the cleanest app-native experience.</p>
            </div>
            <button className="inline-flex min-h-[44px] items-center justify-center rounded-full bg-[#D4AF37] px-4 py-2 text-sm font-bold text-[#1A1A1A]" data-testid="mobile-install-banner-cta" onClick={handleInstall} type="button">
              {iosSafari ? "Guide" : deferredPrompt ? "Install" : "Guide"}
            </button>
            <button className="inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full border border-[#8b6d16]/15 bg-white/65 text-[#8b6d16]" data-testid="mobile-install-banner-close-button" onClick={dismissBanner} type="button">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      ) : (
        <div className="mobile-install-banner mt-2 rounded-[24px] border border-[#D4AF37]/28 bg-[#FDF5E6] px-4 py-4 shadow-[0_14px_34px_rgba(90,75,53,0.08)] md:hidden" data-testid="mobile-install-banner">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#D4AF37]/18 text-[#8b6d16]" data-testid="mobile-install-banner-icon">
              <Download className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#8b6d16]" data-testid="mobile-install-banner-kicker">Install SG Prop Portal</p>
              <p className="mt-1 text-sm leading-6 text-[#3f3a32]" data-testid="mobile-install-banner-copy">
                Add this portal to your home screen for a cleaner, faster app-native experience.
              </p>
              <div className="mt-3 flex items-center gap-3">
                <button className="inline-flex min-h-[44px] items-center justify-center rounded-full bg-[#D4AF37] px-4 py-2 text-sm font-bold text-[#1A1A1A]" data-testid="mobile-install-banner-cta" onClick={handleInstall} type="button">
                  {iosSafari ? "Install Guide" : deferredPrompt ? "Install App" : "Install Guide"}
                </button>
                <button className="inline-flex min-h-[44px] items-center justify-center rounded-full border border-[#8b6d16]/18 bg-white/70 px-4 py-2 text-sm font-semibold text-[#8b6d16]" data-testid="mobile-install-banner-dismiss-button" onClick={dismissBanner} type="button">
                  Not now
                </button>
              </div>
            </div>
            <button className="inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full border border-[#8b6d16]/15 bg-white/65 text-[#8b6d16]" data-testid="mobile-install-banner-close-button" onClick={dismissBanner} type="button">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {showIosOverlay ? (
        <div className="fixed inset-0 z-[120] flex items-end justify-center bg-[#001935]/45 px-4 pb-6 pt-10 md:hidden" data-testid="ios-install-instructions-overlay">
          <div className="w-full max-w-sm rounded-[28px] border border-[#D4AF37]/28 bg-[#FDF5E6] p-5 shadow-[0_24px_60px_rgba(0,25,53,0.24)]">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#8b6d16]">Install on iPhone</p>
                <h3 className="mt-2 text-2xl font-semibold text-[#1A1A1A]">Add to Home Screen</h3>
              </div>
              <button className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-[#8b6d16]/15 bg-white/70 text-[#8b6d16]" data-testid="ios-install-instructions-close-button" onClick={() => setShowIosOverlay(false)} type="button">
                <X className="h-4 w-4" />
              </button>
            </div>
            <ol className="mt-4 space-y-3 text-sm leading-6 text-[#3f3a32]" data-testid="ios-install-instructions-list">
              <li className="rounded-[18px] border border-[#D4AF37]/18 bg-white/70 px-4 py-3"><strong>1.</strong> Tap the <Share2 className="mx-1 inline h-4 w-4 text-[#8b6d16]" /> Share button in Safari.</li>
              <li className="rounded-[18px] border border-[#D4AF37]/18 bg-white/70 px-4 py-3"><strong>2.</strong> Scroll the sheet and tap <strong>Add to Home Screen</strong>.</li>
              <li className="rounded-[18px] border border-[#D4AF37]/18 bg-white/70 px-4 py-3"><strong>3.</strong> Tap <strong>Add</strong> to launch SG Prop Portal like a native app.</li>
            </ol>
          </div>
        </div>
      ) : null}
    </>
  );
};